import { useState } from "react";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { toast } from "react-toastify";
import {
  getLoginMarketingLinks,
  GRADIENT_BUTTON_STYLE,
} from "@constants/loginMarketingLinks";
import { useTranslation } from "react-i18next";
import { subscribe } from "api/mailChimpSubscription";

const FOOTER_BG = "#0B1220";

const FOOTER_LINKS_LEFT = [
  { label: "Cookie Statement", key: "cookieStatement" },
  { label: "Privacy Policy", key: "privacyPolicy" },
  { label: "Terms & Conditions", key: "terms" },
];

const FOOTER_LINKS_RIGHT = [
  { label: "FAQ", key: "faq" },
  { label: "Contact us", key: "contact" },
];

const SOCIAL_LINKS = [
  // {
  //   label: "X",
  //   href: "",
  //   icon: (
  //     <svg
  //       className="w-6 h-6"
  //       viewBox="0 0 24 24"
  //       fill="#EBEBEB"
  //       aria-hidden
  //     >
  //       <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  //     </svg>
  //   ),
  // },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61571062035394",
    icon: <Facebook className="w-6 h-6" color="#EBEBEB" strokeWidth={2} />,
  },
  // {
  //   label: "LinkedIn",
  //   href: "",
  //   icon: <Linkedin className="w-6 h-6" color="#EBEBEB" strokeWidth={2} />,
  // },
  {
    label: "Instagram",
    href: "https://www.instagram.com/budgetfitplannl/",
    icon: <Instagram className="w-6 h-6" color="#EBEBEB" strokeWidth={2} />,
  },
];


const LoginMarketingFooter = () => {
  const { t } = useTranslation();
  const links = getLoginMarketingLinks();
  const [subscribeName, setSubscribeName] = useState("");
  const [subscribeEmail, setSubscribeEmail] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeName.trim() || !subscribeEmail.trim()) {
      toast.error(t("Please enter your name and email."));
      return;
    }
    try {
      await subscribe(subscribeName, subscribeEmail);

      toast.success(t("Thank you for subscribing!"));
      setSubscribeName("");
      setSubscribeEmail("");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        t("Failed to subscribe. Please try again.")
      );
    }
  };

  const footerInputClass =
    "w-full px-3 py-2.5 rounded-md bg-[#1a2332] border border-[#2d3a4d] text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-[#15AAFC] focus:border-[#15AAFC]";

  return (
    <footer
      className="w-full !font-[Lato]"
      style={{ backgroundColor: FOOTER_BG }}
    >
      {/* CTA band */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
        <h2 className="text-2xl sm:text-[30px] font-[600] text-[#0575E6] mb-3">
          {t("No long-term contracts. No catches. Simple.")}
        </h2>
        <p className="text-[#EBEBEB] text-sm sm:text-[20px] mb-8">
          {t("Start your 7-day free trial. Cancel anytime.")}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={links.aboutUs}
            className="inline-flex items-center justify-center min-w-[140px] px-6 py-3 rounded-lg bg-white text-[#0575E6] text-lg font-bold hover:bg-gray-100 transition-colors"
            // target="_blank"
            rel="noopener noreferrer"
          >
            {t("About us")}
          </a>
          <a
            href={links.getStarted}
            className="inline-flex items-center justify-center min-w-[180px] px-6 py-3 rounded-lg text-white text-lg font-bold shadow-md hover:shadow-lg transition-shadow bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)]"
            // target="_blank"
            rel="noopener noreferrer"
          >
            {t("Get Started Now")}
          </a>
        </div>
      </div>

      {/* Footer grid */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 border-t border-[#E9EAEB] ">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-4">
            <a
              href={links.home}
              // target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4"
            >
              <img src="/whiteBlueLogo.png" alt="Budget Fit Plan" />
            </a>
            <p className="text-white text-sm leading-relaxed max-w-xs">
              {t("Create value-packed plans that fit your budget and bring happiness to life!")}
            </p>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 gap-6 sm:gap-8">
            <ul className="space-y-3">
              {FOOTER_LINKS_LEFT.map(({ label, key }) => (
                <li key={key}>
                  <a
                    href={links[key]}
                    className="text-white text-md font-[600] hover:text-[#15AAFC] transition-colors"
                    // target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(label)}
                  </a>
                </li>
              ))}
            </ul>
            <ul className="space-y-3">
              {FOOTER_LINKS_RIGHT.map(({ label, key }) => (
                <li key={key}>
                  <a
                    href={links[key]}
                    className="text-white text-md font-[600] hover:text-[#15AAFC] transition-colors"
                    // target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="text-[#0575e6] text-center font-bold text-[24px] mb-4">
              {t("Subscribe Now")}
            </h3>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="text"
                name="name"
                placeholder={t("Name")}
                value={subscribeName}
                onChange={(e) => setSubscribeName(e.target.value)}
                className={footerInputClass}
                autoComplete="name"
              />
              <input
                type="email"
                name="email"
                placeholder={t("Email")}
                value={subscribeEmail}
                onChange={(e) => setSubscribeEmail(e.target.value)}
                className={footerInputClass}
                autoComplete="email"
              />
              <button
                type="submit"
                className="w-full py-2.5 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] opacity-70 rounded-md text-white text-sm font-semibold"
                style={GRADIENT_BUTTON_STYLE}
                // disabled={true}
              >
                {t("Subscribe")}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-[1280px] mx-auto border-t border-[#E9EAEB]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-xs sm:text-sm text-center sm:text-left">
            © {new Date().getFullYear()} Budget Fit Plan. {t("All rights reserved.")}
          </p>
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                className="text-white/70 hover:text-white transition-colors p-1"
                // target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
              >
                {icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LoginMarketingFooter;
